import type { CapabilityId, HumanRoleId } from '../../domain/access/rolePreset'
import type { PermissionPolicy } from '../../domain/access/permissionEngine'
import {
  resolveConfiguration,
  type ConfigurationLayer,
  type ConfigurationLayerId,
  type ConfigurationSchema,
} from '../../domain/configuration/configurationResolver'
import {
  buildResolvedExperiencePreview,
  type ResolvedExperiencePreview,
} from '../../domain/preview/resolvedExperiencePreview'
import { previewRuntimeProvider } from '../../integrations/supabase/previewRuntimeProvider'
import { mapProviderError, type AppResult } from '../platform/public'

type RuntimeRow = Record<string, unknown>

export type PreviewRuntimeResult = Readonly<{
  preview: ResolvedExperiencePreview
  fieldVisibility: readonly Readonly<{
    objectType: string
    field: string
    visibility: string
  }>[]
}>

function configurationLayer(
  id: ConfigurationLayerId,
  row: RuntimeRow | undefined,
): ConfigurationLayer | null {
  if (!row) return null
  const schemaVersion = Number(row.schema_version)
  const payload = row.payload
  if (!Number.isInteger(schemaVersion) || !payload || typeof payload !== 'object' || Array.isArray(payload)) return null
  return { id, schemaVersion, values: payload as Record<string, unknown> }
}

function activeVersion(
  pointers: RuntimeRow[],
  versions: RuntimeRow[],
  scopeType: string,
  scopeKey: string,
): RuntimeRow | undefined {
  const pointer = pointers.find((row) => row.scope_type === scopeType && row.scope_key === scopeKey)
  if (!pointer) return undefined
  return versions.find((row) => Number(row.id) === Number(pointer.configuration_version_id))
}

function permissionPolicies(rows: RuntimeRow[], roleId: HumanRoleId): PermissionPolicy[] {
  return rows
    .filter((row) => row.role_id === roleId)
    .map((row) => ({
      roleId,
      capability: String(row.capability_id) as CapabilityId,
      objectType: String(row.object_type),
      action: String(row.action),
      decision: String(row.decision) as PermissionPolicy['decision'],
      ...(typeof row.module_id === 'string' && row.module_id ? { moduleId: row.module_id } : {}),
    }))
}

export const previewApi = {
  async load(input: {
    tenantId: string
    userId: string
    roleId: HumanRoleId
    capabilities: readonly string[]
    pinnedMetric?: string | null
  }): Promise<AppResult<PreviewRuntimeResult>> {
    const result = await previewRuntimeProvider.load(input.tenantId, input.roleId)
    if (result.error || !result.data) return { data: null, error: mapProviderError(result.error) }

    const schemaRow = result.data.schema as RuntimeRow | null
    if (!schemaRow) return { data: null, error: { code: 'INVALID', message: 'configuration_schema_missing' } }

    const schema: ConfigurationSchema = {
      version: Number(schemaRow.schema_version),
      allowedKeys: new Set(Array.isArray(schemaRow.allowed_keys) ? schemaRow.allowed_keys.map(String) : []),
      protectedKeys: new Set(Array.isArray(schemaRow.protected_keys) ? schemaRow.protected_keys.map(String) : []),
    }

    const pointers = result.data.pointers as RuntimeRow[]
    const versions = result.data.versions as RuntimeRow[]
    const layers: ConfigurationLayer[] = []
    const workspaceLayer = configurationLayer('workspace_preset', activeVersion(pointers, versions, 'workspace', 'workspace'))
    const roleLayer = configurationLayer('role_preset', activeVersion(pointers, versions, 'role', input.roleId))
    const userLayer = configurationLayer('user_override', activeVersion(pointers, versions, 'user', input.userId))
    if (workspaceLayer) layers.push(workspaceLayer)
    if (roleLayer) layers.push(roleLayer)
    if (userLayer) layers.push(userLayer)

    let configuration
    try {
      configuration = resolveConfiguration(layers, schema)
    } catch (error) {
      return {
        data: null,
        error: { code: 'INVALID', message: error instanceof Error ? error.message : 'configuration_invalid' },
      }
    }

    const enabledModules = new Set(
      (result.data.modules as RuntimeRow[])
        .filter((row) => row.state === 'enabled')
        .map((row) => String(row.module_id)),
    )
    const policies = permissionPolicies(result.data.policies as RuntimeRow[], input.roleId)
    const capabilities = new Set(input.capabilities as CapabilityId[])
    const actions = policies.map((policy) => ({
      id: `${policy.objectType}.${policy.action}`,
      context: {
        roleId: input.roleId,
        capabilities,
        actorWorkspaceId: input.tenantId,
        target: {
          workspaceId: input.tenantId,
          objectType: policy.objectType,
          objectId: `preview:${policy.objectType}`,
        },
        action: policy.action,
        enabledModules,
      },
      policies,
    }))

    const preview = buildResolvedExperiencePreview({
      userId: input.userId,
      roleId: input.roleId,
      configuration,
      configurationState: { valid: true },
      previewPolicy: {
        purpose: 'admin_explainability',
        objectScope: {
          workspaceId: input.tenantId,
          objectType: 'workspace',
          objectId: input.tenantId,
        },
      },
      enabledModules,
      pinnedMetric: input.pinnedMetric,
      actions,
    })

    const fieldVisibility = (result.data.visibility as RuntimeRow[]).map((row) => ({
      objectType: String(row.object_type),
      field: String(row.field_key),
      visibility: String(row.visibility_class),
    }))

    return { data: { preview, fieldVisibility }, error: null }
  },
}
